import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Repeat, Calendar, Package } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface ReorderItem {
  id: string;
  product_name: string;
  category: string | null;
  cycle_days: number;
  next_reorder_date: string | null;
  last_price: string | null;
  last_retailer: string | null;
  status: string;
}

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function urgencyClass(days: number | null): string {
  if (days === null) return "text-muted-foreground";
  if (days <= 0) return "text-score-low";
  if (days <= 7) return "text-score-mid";
  return "text-score-high";
}

export default function ReorderSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<ReorderItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("reorder_items")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("next_reorder_date", { ascending: true });
      setItems((data as ReorderItem[]) ?? []);
      setLoading(false);
    })();
  }, [user]);

  if (!user || loading || items.length === 0) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
      <div className="flex items-center gap-2 mb-3">
        <Repeat className="w-4 h-4 text-accent" />
        <span className="text-sm font-display font-semibold text-foreground">Repeat buys</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-body">{items.length}</span>
      </div>

      <div className="space-y-2">
        {items.map((item) => {
          const days = daysUntil(item.next_reorder_date);
          const urgency = urgencyClass(days);

          return (
            <div key={item.id} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-body font-medium text-foreground truncate">{item.product_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {item.category && (
                    <span className="text-[9px] text-muted-foreground/60 font-body capitalize">{item.category}</span>
                  )}
                  <span className="text-[9px] text-muted-foreground/60 font-body">Every {item.cycle_days}d</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right">
                  <p className={`text-[11px] font-body font-medium ${urgency}`}>
                    {days === null ? "No date set" : days <= 0 ? "Due now" : `${days}d left`}
                  </p>
                  {item.last_price && (
                    <p className="text-[9px] text-muted-foreground/50 font-body">Last: {item.last_price}</p>
                  )}
                </div>
                <button
                  onClick={() => navigate(`/?q=${encodeURIComponent(item.product_name)}`)}
                  className="p-1.5 rounded-lg hover:bg-muted/30 transition-colors"
                  title="Recheck now"
                >
                  <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
